import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { playUrl, stopAudio, ttsUrl, speakFallback, FATAL_SR_ERRORS } from "@/lib/audio";
import { Bubbles, VoiceWave } from "@/components/Bubbles";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { InstallPWA } from "@/components/InstallPWA";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Home, Car, FileText, Sparkles, Bell, ArrowRight,
  Building2, ScanLine, Repeat, Mic, Send, HeartPulse,
  User, Volume2, VolumeX, Camera, Mail, FolderLock,
} from "lucide-react";

const FEATURES = [
  { icon: Home, title: "Passeport du logement", text: "Pièces, équipements, entretiens, installateurs, garanties — la mémoire complète de votre maison." },
  { icon: Car, title: "Carnet de vie véhicule", text: "Kilométrage, pneus, révisions, contrôle technique. Échéances temporelles ET kilométriques." },
  { icon: ScanLine, title: "Coffre documentaire OCR", text: "Photographiez un ticket, une facture, un contrat. OMY extrait tout et classe pour vous." },
  { icon: ShieldCheck, title: "Garanties & assurances", text: "OMY suit vos garanties et vous prévient avant l'expiration. Sans jamais rien inventer." },
  { icon: Bell, title: "Rappels intelligents", text: "À savoir, à prévoir, à faire, urgent. OMY regroupe pour éviter la fatigue de notifications." },
  { icon: Repeat, title: "Transfert de propriété", text: "Vendez un bien avec son passeport OMY. L'historique transférable suit l'objet." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wlEmail, setWlEmail] = useState("");
  const [wlBusy, setWlBusy] = useState(false);
  const [wlDone, setWlDone] = useState(false);
  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!wlEmail.trim()) return;
    setWlBusy(true);
    try {
      const res = await fetch(`${API}/waitlist`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: wlEmail.trim() }) });
      if (res.ok) setWlDone(true);
    } catch (err) {} finally { setWlBusy(false); }
  };
  const [demoText, setDemoText] = useState("");
  const [demoReply, setDemoReply] = useState(null);
  const [audioOn, setAudioOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const speakTokenRef = useRef(0);

  useEffect(() => {
    return () => {
      stopAudio();
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} }
    };
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const stripEmoji = (t) => t.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}👇]/gu, "").trim();

  const fallbackSpeak = (clean) => speakFallback(clean, { onstart: () => setSpeaking(true), onend: () => setSpeaking(false) });

  const speak = async (text) => {
    if (!audioOn) return;
    const clean = stripEmoji(text);
    if (!clean) return;
    const token = (speakTokenRef.current += 1);
    setSpeaking(true);
    try {
      await playUrl(ttsUrl(clean));
      if (speakTokenRef.current === token) setSpeaking(false);
    } catch (e) {
      if (speakTokenRef.current === token) fallbackSpeak(clean);
    }
  };

  const ask = (q) => {
    setDemoText(q);
    const reply = /loto|numéros|avenir|futur/i.test(q)
      ? "Ça, même moi je ne peux pas ! 😄 Mais je peux te dire que la garantie de ton téléviseur expire dans 23 jours."
      : "Bonne question ! Connecte-toi et je te réponds à partir de ta propre mémoire OMY. 👇";
    setDemoReply(reply);
    speak(reply);
  };

  const LISTEN_SAMPLES = [
    "Hey Omi, jusqu'à quand mon frigo est garanti ?",
    "Hey Omi, quand dois-je changer la pile du détecteur ?",
    "Hey Omi, dans combien de kilomètres dois-je faire ma révision ?",
    "Hey Omi, retrouve-moi la facture de ma télé.",
  ];

  const stopListening = () => {
    setListening(false);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} }
  };

  const startListening = () => {
    if (listening) { stopListening(); return; }
    stopAudio();
    setSpeaking(false);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const rec = new SR();
      rec.lang = "fr-FR"; rec.interimResults = true; rec.continuous = false;
      recognitionRef.current = rec;
      let finalText = "";
      rec.onstart = () => { setListening(true); setDemoText(""); };
      rec.onresult = (e) => {
        let t = "";
        for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
        finalText = t; setDemoText(t);
      };
      rec.onerror = (e) => {
        setListening(false);
        if (FATAL_SR_ERRORS.includes(e.error)) setDemoText("Micro inaccessible : autorise le micro pour ce site dans les réglages du navigateur.");
      };
      rec.onend = () => { setListening(false); if (finalText.trim()) ask(finalText.trim()); };
      try { rec.start(); } catch (e) { setListening(false); }
    } else {
      setListening(true);
      const q = LISTEN_SAMPLES[Math.floor(Math.random() * LISTEN_SAMPLES.length)];
      let i = 0;
      const iv = setInterval(() => { i += 1; setDemoText(q.slice(0, Math.ceil((q.length * i) / 14))); }, 110);
      setTimeout(() => { clearInterval(iv); setDemoText(q); setListening(false); ask(q); }, 1900);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 omy-glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 h-16 grid grid-cols-3 items-center">
          <div className="justify-self-start"><Logo /></div>
          <div className="justify-self-center">
            {!user && (
              <Button data-testid="nav-cta-btn" onClick={() => navigate("/connexion?mode=register")}
                className="bg-primary hover:bg-primary/90 rounded-full px-5 sm:px-7 h-10">
                <Sparkles size={16} className="mr-1.5" />
                <span className="hidden sm:inline">Démarrer gratuitement</span>
                <span className="sm:hidden">Démarrer</span>
              </Button>
            )}
          </div>
          <div className="justify-self-end">
            <button data-testid="nav-login-btn" onClick={() => navigate(user ? "/app" : "/connexion")} aria-label={user ? "Mon espace" : "Se connecter"}
              className="w-10 h-10 rounded-full omy-glass flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-colors">
              <User size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 pb-24 min-h-screen flex items-center overflow-hidden">
        <div className="aurora" style={{ width: 520, height: 520, background: "#E05A36", top: -120, left: -80 }} />
        <div className="aurora" style={{ width: 480, height: 480, background: "#F59E0B", bottom: -140, right: -60, opacity: 0.25 }} />
        <Bubbles onPick={ask} />

        <div className="relative z-10 w-full max-w-3xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-2 rounded-full omy-glass px-4 py-1.5 text-xs text-muted-foreground mb-7 animate-fadeUp">
            <Sparkles size={14} className="text-primary" /> Omy, mon assistant à vie.
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] animate-fadeUp">
            Votre vie a une <span className="text-gradient">mémoire</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto animate-fadeUp" style={{ animationDelay: "0.1s" }}>
            Vous n'avez plus besoin de vous souvenir de tout. <span className="text-foreground font-medium">OMY le fait pour vous.</span>
          </p>

          {/* Assistant demo bar */}
          <div className="mt-10 omy-glass omy-glow rounded-3xl p-3 max-w-xl mx-auto animate-fadeUp" style={{ animationDelay: "0.2s" }}>
            <div className="flex items-center gap-3 px-3">
              <button data-testid="hero-mic-btn" onClick={startListening} aria-label="Parler à OMY"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${listening ? "" : "pulse-ring"}`}
                style={{ background: listening ? "linear-gradient(135deg,#EF4444,#F97316)" : "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
                <Mic size={18} className="text-white" />
              </button>
              <input
                data-testid="hero-ask-input"
                value={demoText}
                onChange={(e) => setDemoText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask(demoText)}
                placeholder="Hey Omi, qu'est-ce que je dois savoir aujourd'hui ?"
                className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground py-3"
              />
              {(listening || speaking) && <VoiceWave active bars={5} />}
              <button data-testid="hero-audio-toggle" aria-label="Activer le son"
                onClick={() => { const n = !audioOn; setAudioOn(n); if (!n) { stopAudio(); setSpeaking(false); } }}
                className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0">
                {audioOn ? <Volume2 size={18} className={speaking ? "text-primary" : ""} /> : <VolumeX size={18} />}
              </button>
              <button data-testid="hero-ask-send" onClick={() => ask(demoText)}
                className="w-9 h-9 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 shrink-0">
                <Send size={16} className="text-white" />
              </button>
            </div>
            {listening && (
              <div data-testid="hero-listening" className="flex items-center gap-3 mt-2 mx-2 rounded-2xl bg-red-500/10 border border-red-500/20 px-4 py-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                <span className="text-sm text-foreground/90">OMY écoute…</span>
                <div className="ml-auto"><VoiceWave active bars={7} /></div>
              </div>
            )}
            {demoReply && !listening && (
              <div data-testid="hero-demo-reply" className="flex items-start gap-3 text-left mt-2 mx-2 rounded-2xl bg-black/30 px-4 py-3 text-sm text-foreground/90">
                <button data-testid="hero-replay-btn" onClick={() => speak(demoReply)} aria-label="Réécouter"
                  className="mt-0.5 text-primary shrink-0 hover:scale-110 transition-transform">
                  {speaking ? <VoiceWave active bars={4} /> : <Volume2 size={16} />}
                </button>
                <span>{demoReply}</span>
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">Démo — posez une question, OMY vous répond à voix haute.</p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {["Hey Omi, donne-moi les numéros du Loto 🎲", "Mon frigo est-il garanti ?", "Quand entretenir ma clim ?"].map((s, i) => (
              <button key={i} data-testid={`hero-suggestion-${i}`} onClick={() => ask(s)}
                className="text-xs rounded-full border border-white/10 px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="capacites" className="relative z-10 max-w-7xl mx-auto px-5 py-20">
        <p className="reveal font-mono text-xs uppercase tracking-widest text-primary mb-3">Ce qu'OMY retient pour vous</p>
        <h2 className="reveal font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl" style={{ animationDelay: "80ms" }}>
          Parlez, prenez une photo, transférez un e-mail. OMY fait le reste.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {FEATURES.map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`} style={{ animationDelay: `${i * 70}ms` }}
              className="reveal omy-glass rounded-3xl p-7 hover:border-primary/40 transition-colors">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                style={{ background: "hsl(13 74% 54% / 0.14)" }}>
                <f.icon size={20} className="text-primary" />
              </div>
              <h3 className="font-display text-lg font-medium mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="comment" className="relative z-10 max-w-7xl mx-auto px-5 py-16">
        <p className="reveal font-mono text-xs uppercase tracking-widest text-primary mb-3">Comment ça marche</p>
        <h2 className="reveal font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl" style={{ animationDelay: "80ms" }}>
          Trois façons simples de tout confier à OMY.
        </h2>
        <div className="grid sm:grid-cols-3 gap-5 mt-12">
          {[
            { icon: Mic, step: "1", title: "Parle à OMY", text: "Dis « Hey Omi » et raconte : un achat, une réparation, un rappel. OMY comprend et mémorise à ta place." },
            { icon: Camera, step: "2", title: "Scanne un ticket ou une photo", text: "Photographie une facture, un ticket ou un appareil : OMY lit tout, remplit la fiche et suit la garantie." },
            { icon: Mail, step: "3", title: "Boîte e-mail sécurisée", text: "Reçois tes tickets sur ton alias privé @omyemail — ils se classent tout seuls dans ton coffre-fort." },
          ].map((s, i) => (
            <div key={i} data-testid={`howto-step-${i}`} style={{ animationDelay: `${i * 70}ms` }} className="reveal omy-glass rounded-3xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "hsl(13 74% 54% / 0.14)" }}><s.icon size={20} className="text-primary" /></div>
                <span className="font-mono text-xs text-muted-foreground">Étape {s.step}</span>
              </div>
              <h3 className="font-display text-lg font-medium mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        <div data-testid="vault-explainer" className="reveal omy-glass rounded-[2rem] p-8 sm:p-10 mt-6 flex flex-col md:flex-row gap-6 md:items-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
            <FolderLock size={30} className="text-white" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium mb-2">Ton coffre-fort de documents</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              Chaque facture, garantie, notice ou contrat est rangé et chiffré au bon endroit. Besoin de la facture de ta télé, ou de savoir jusqu'à quand ton frigo est garanti ? Demande-le à OMY en une phrase — il retrouve le document et la date en un instant.
            </p>
          </div>
        </div>
      </section>

      {/* Memory extension — emotional */}
      <section data-testid="memory-extension-section" className="relative z-10 max-w-7xl mx-auto px-5 py-16">
        <div className="omy-glass rounded-[2rem] overflow-hidden grid md:grid-cols-2 items-stretch">
          <div className="relative min-h-[280px] md:min-h-[420px]">
            <img src="https://images.unsplash.com/photo-1624448445915-97154f5e688c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Famille heureuse partageant un moment" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, transparent 40%, hsl(222 14% 9% / 0.9) 100%)" }} />
            <div className="absolute inset-0 md:hidden" style={{ background: "linear-gradient(0deg, hsl(222 14% 9%) 5%, transparent 60%)" }} />
          </div>
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <p className="reveal font-mono text-xs uppercase tracking-widest text-primary mb-3">Votre extension de mémoire</p>
            <h2 className="reveal font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-[1.1]" style={{ animationDelay: "80ms" }}>
              Oubliez les détails. <span className="text-gradient">Gardez les souvenirs.</span>
            </h2>
            <p className="reveal mt-5 text-base text-muted-foreground leading-relaxed max-w-md" style={{ animationDelay: "160ms" }}>
              OMY est votre mémoire annexe : garanties, entretiens, papiers, échéances — il retient tout ce qui doit l'être.
              <span className="text-foreground font-medium"> Votre esprit reste libre pour l'essentiel : les beaux moments en famille.</span>
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Zéro charge mentale", "Rien ne se perd", "Plus de temps pour les vôtres"].map((t, i) => (
                <span key={i} className="text-xs rounded-full border border-white/10 px-3 py-1.5 text-muted-foreground">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Health + business strip */}
      <section id="business" className="relative z-10 max-w-7xl mx-auto px-5 py-16 grid md:grid-cols-2 gap-6">
        <div className="reveal omy-glass rounded-3xl p-8">
          <HeartPulse className="text-accent mb-4" />
          <h3 className="font-display text-xl font-medium mb-2">Rappels santé protégés</h3>
          <p className="text-sm text-muted-foreground">Module isolé et chiffré. OMY vous rappelle vos prises mais n'improvise jamais une prescription ni un diagnostic.</p>
        </div>
        <div className="reveal omy-glass rounded-3xl p-8" style={{ animationDelay: "90ms" }}>
          <Building2 className="text-primary mb-4" />
          <h3 className="font-display text-xl font-medium mb-2">OMY Business</h3>
          <p className="text-sm text-muted-foreground">Fabricants, revendeurs, réparateurs, assureurs : passeports numériques, documentation, cycle de vie produit. OMY reste gratuit pour le particulier.</p>
        </div>
      </section>

      {/* CTA */}
      <section id="confidentialite" className="relative z-10 max-w-4xl mx-auto px-5 py-24 text-center">
        <div className="inline-flex items-center gap-2 rounded-full omy-glass px-4 py-1.5 text-xs text-muted-foreground mb-6">
          <ShieldCheck size={14} className="text-accent" /> Vos données ne sont jamais le produit
        </div>
        <h2 className="reveal font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Commencez à déléguer votre mémoire.
        </h2>
        <p className="reveal mt-4 text-muted-foreground" style={{ animationDelay: "80ms" }}>Gratuit pour les particuliers. Aucune carte requise.</p>
        <Button data-testid="cta-final-btn" onClick={() => navigate("/connexion?mode=register")}
          className="mt-8 bg-primary hover:bg-primary/90 rounded-full px-7 h-12 text-base">
          Créer ma mémoire OMY <ArrowRight size={18} className="ml-2" />
        </Button>
      </section>

      {/* Mobile app — coming soon */}
      <section data-testid="mobile-app-banner" className="relative z-10 max-w-4xl mx-auto px-5 pb-10">
        <div className="omy-glass rounded-3xl px-6 py-10 sm:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-medium mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Prochainement disponible
          </div>
          <h2 className="reveal font-display text-2xl sm:text-3xl font-semibold tracking-tight">Bientôt dans votre poche</h2>
          <p className="reveal mt-3 text-muted-foreground text-sm sm:text-base max-w-lg mx-auto" style={{ animationDelay: "80ms" }}>
            L'application mobile OMY arrive sur iOS et Android — emmenez votre mémoire partout, même hors connexion.
          </p>
          <div className="mt-6 flex justify-center">
            <InstallPWA label="Installer OMY maintenant" />
          </div>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button data-testid="appstore-btn" disabled aria-disabled="true" title="Prochainement disponible"
              className="relative w-full sm:w-auto inline-flex items-center gap-3 rounded-xl bg-black border border-white/15 px-5 py-3 text-left cursor-not-allowed opacity-80 hover:opacity-100 transition-opacity">
              <svg viewBox="0 0 384 512" width="24" height="24" fill="#fff" aria-hidden="true"><path d="M318.7 268c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141 4 184.6 4 273.5q0 39.4 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-92.6zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
              <span className="text-white leading-tight">
                <span className="block text-[10px] text-white/60">Bientôt sur l'</span>
                <span className="block text-base font-semibold -mt-0.5">App Store</span>
              </span>
              <span className="absolute -top-2 -right-2 text-[9px] font-semibold bg-primary text-white rounded-full px-2 py-0.5">Prochainement</span>
            </button>
            <button data-testid="playstore-btn" disabled aria-disabled="true" title="Prochainement disponible"
              className="relative w-full sm:w-auto inline-flex items-center gap-3 rounded-xl bg-black border border-white/15 px-5 py-3 text-left cursor-not-allowed opacity-80 hover:opacity-100 transition-opacity">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff" aria-hidden="true"><path d="M4 3.5v17a1 1 0 0 0 1.5.87l14.5-8.5a1 1 0 0 0 0-1.74L5.5 2.63A1 1 0 0 0 4 3.5z"/></svg>
              <span className="text-white leading-tight">
                <span className="block text-[10px] text-white/60">Bientôt sur</span>
                <span className="block text-base font-semibold -mt-0.5">Google Play</span>
              </span>
              <span className="absolute -top-2 -right-2 text-[9px] font-semibold bg-primary text-white rounded-full px-2 py-0.5">Prochainement</span>
            </button>
          </div>

          <div className="mt-7 max-w-sm mx-auto">
            {wlDone ? (
              <p data-testid="waitlist-success" className="text-sm text-emerald-400 font-medium">Merci ! On vous préviendra dès le lancement.</p>
            ) : (
              <form data-testid="waitlist-form" onSubmit={submitWaitlist} className="flex flex-col sm:flex-row gap-2">
                <input data-testid="waitlist-email" type="email" required value={wlEmail} onChange={(e) => setWlEmail(e.target.value)}
                  placeholder="Votre e-mail" className="flex-1 h-11 rounded-full bg-black/40 border border-white/10 px-4 text-sm outline-none focus:border-primary/50" />
                <Button data-testid="waitlist-submit" type="submit" disabled={wlBusy} className="bg-primary hover:bg-primary/90 rounded-full h-11 px-5 shrink-0">
                  {wlBusy ? "…" : "Préviens-moi au lancement"}
                </Button>
              </form>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Zéro spam — juste un message le jour du lancement.</p>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-2 mb-2"><Logo size={20} /></div>
        hey-omy.app — Omy, mon assistant à vie.
      </footer>
    </div>
  );
}
